import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getCandidates,
  createCandidate,
  deleteCandidate,
  searchCandidates,
  testUpload
} from '../services/api';
import { DocumentArrowUpIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const Candidates = () => {
  const [jobDescription, setJobDescription] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [screeningResult, setScreeningResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Add Candidate Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingCandidate, setAddingCandidate] = useState(false);
  const [newCandidate, setNewCandidate] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    skills: '',
    experience: '',
    location: '',
    source: 'Manual',
    notes: ''
  });

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await getCandidates();
      const data = response.data;
      setCandidates(Array.isArray(data) ? data : data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    if (!newCandidate.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setAddingCandidate(true);
    try {
      const candidateData = {
        ...newCandidate,
        skills: newCandidate.skills ? newCandidate.skills.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      const response = await createCandidate(candidateData);
      setCandidates(prev => [response.data, ...prev]);
      toast.success('Candidate added successfully!');
      setShowAddModal(false);
      setNewCandidate({ name: '', email: '', phone: '', title: '', skills: '', experience: '', location: '', source: 'Manual', notes: '' });
    } catch (error) {
      console.error('Error creating candidate:', error);
      toast.error(error.response?.data?.message || 'Failed to add candidate');
    } finally {
      setAddingCandidate(false);
    }
  };

  const handleDeleteCandidate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      await deleteCandidate(id);
      setCandidates(candidates.filter(c => c.id !== id));
      toast.success('Candidate deleted');
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Failed to delete candidate');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!jobDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }

    setIsLoading(true);
    try {
      const response = await searchCandidates(jobDescription);
      if (response.data && response.data.candidates) {
        setCandidates(response.data.candidates);
        if (response.data.candidates.length === 0) {
          toast.info('No matching candidates found');
        }
      }
    } catch (error) {
      console.error('Search candidates error:', error);
      toast.error('Failed to search candidates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      setSelectedFile(file);
      toast.success('Resume selected: ' + file.name);
    } else if (file) {
      toast.error('Please select a PDF file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setSelectedFile(file);
        toast.success('Resume dropped: ' + file.name);
      } else {
        toast.error('Please drop a PDF file');
      }
    }
  };

  const handleResumeScreening = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error('Please select a resume file');
      return;
    }

    if (!jobDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }

    setIsLoading(true);

    try {
      toast.info('Testing file upload capability first...');
      try {
        const testResult = await testUpload(selectedFile);
        if (!testResult.success) {
          throw new Error('Test upload failed');
        }
        toast.info('File upload test successful, proceeding with screening');
      } catch (testErr) {
        console.error('Test upload error:', testErr);
        throw new Error('File upload test failed. The system cannot process your file.');
      }

      const formData = new FormData();
      formData.append('resume', selectedFile, selectedFile.name);
      formData.append('jobDescription', jobDescription);

      const token = localStorage.getItem('token');

      if (!token && process.env.NODE_ENV === 'development') {
        localStorage.setItem('token', 'mock-token-for-development');
      }

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token') || 'mock-token-for-development'}`
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/candidates/screen-resume`, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data) {
        setScreeningResult(data);
        toast.success('Resume screened successfully!');
      } else {
        toast.error('Received empty response from server');
      }
    } catch (error) {
      console.error('Resume screening error:', error);
      toast.error(error.message || 'Error processing resume. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Candidates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Search for candidates, screen resumes, and manage your talent pipeline.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          Add Candidate
        </button>
      </div>

      {/* Search Form */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700">
                Job Description
              </label>
              <textarea
                id="jobDescription"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter job description to search for matching candidates..."
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {isLoading ? 'Searching...' : 'Search Candidates'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Resume Screening */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Resume Screening</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>Upload a resume to screen against the job description.</p>
          </div>
          <form onSubmit={handleResumeScreening} className="mt-5">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">Resume (PDF)</label>
                <div
                  className={`mt-1 flex justify-center rounded-md border-2 ${
                    selectedFile
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-dashed border-gray-300'
                  } px-6 pt-5 pb-6`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <div className="space-y-1 text-center">
                    <DocumentArrowUpIcon
                      className={`mx-auto h-12 w-12 ${
                        selectedFile ? 'text-indigo-500' : 'text-gray-400'
                      }`}
                    />
                    <div className="flex flex-col sm:flex-row text-sm text-gray-600 items-center justify-center">
                      <label
                        htmlFor="resume-upload"
                        className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500"
                      >
                        <span>{selectedFile ? 'Change file' : 'Upload a file'}</span>
                        <input
                          id="resume-upload"
                          name="resume-upload"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      {!selectedFile && <p className="pl-1 mt-2 sm:mt-0">or drag and drop</p>}
                    </div>
                    {selectedFile && (
                      <div className="mt-2 text-sm text-indigo-600 font-medium flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Selected: {selectedFile.name}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">PDF up to 10MB</p>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading || !selectedFile}
                className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {isLoading ? 'Screening...' : 'Screen Resume'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Screening Results */}
      {screeningResult && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Screening Results</h3>
            <div className="mt-4">
              <div className="rounded-md bg-gray-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className={`h-12 w-12 rounded-full ${
                      screeningResult.recommendation === 'Strong Match'
                        ? 'bg-green-100'
                        : screeningResult.recommendation === 'Moderate Match'
                        ? 'bg-yellow-100'
                        : 'bg-red-100'
                    } flex items-center justify-center`}>
                      <span className={`text-lg font-semibold ${
                        screeningResult.recommendation === 'Strong Match'
                          ? 'text-green-600'
                          : screeningResult.recommendation === 'Moderate Match'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        {screeningResult.matchScore}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">{screeningResult.recommendation}</h4>
                    <div className="mt-2">
                      <h5 className="text-sm font-medium text-gray-900">Extracted Skills:</h5>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {screeningResult.extractedSkills?.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate List */}
      {candidates.length > 0 && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Candidates ({candidates.length})
              </h3>
              <button
                onClick={fetchCandidates}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Refresh
              </button>
            </div>
            <div className="mt-4">
              <ul className="divide-y divide-gray-200">
                {candidates.map((candidate) => (
                  <li key={candidate.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-lg font-semibold text-indigo-600">
                            {(candidate.name || candidate.fullName || '?').split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {candidate.name || candidate.fullName}
                        </p>
                        {candidate.title && (
                          <p className="text-sm text-gray-500 truncate">{candidate.title}</p>
                        )}
                        {candidate.email && (
                          <p className="text-xs text-gray-400 truncate">{candidate.email}</p>
                        )}
                        {candidate.skills && candidate.skills.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(Array.isArray(candidate.skills) ? candidate.skills : []).slice(0, 5).map((skill, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
                              >
                                {skill}
                              </span>
                            ))}
                            {Array.isArray(candidate.skills) && candidate.skills.length > 5 && (
                              <span className="text-xs text-gray-400">+{candidate.skills.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0">
                        {candidate.matchScore && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Match: {candidate.matchScore}
                          </span>
                        )}
                        {candidate.location && (
                          <span className="text-xs text-gray-500">{candidate.location}</span>
                        )}
                        <button
                          onClick={() => handleDeleteCandidate(candidate.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete candidate"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Empty state when no candidates */}
      {candidates.length === 0 && !isLoading && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a candidate or searching with a job description.</p>
            <div className="mt-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                Add Candidate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Add New Candidate</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateCandidate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={newCandidate.name}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={newCandidate.email}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={newCandidate.phone}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Job Title</label>
                  <input
                    type="text"
                    value={newCandidate.title}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Senior Software Engineer"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Skills</label>
                  <input
                    type="text"
                    value={newCandidate.skills}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, skills: e.target.value }))}
                    placeholder="React, Node.js, Python (comma-separated)"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Separate skills with commas</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Experience</label>
                    <input
                      type="text"
                      value={newCandidate.experience}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, experience: e.target.value }))}
                      placeholder="5 years"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      value={newCandidate.location}
                      onChange={(e) => setNewCandidate(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="San Francisco, CA"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Source</label>
                  <select
                    value={newCandidate.source}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, source: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="Manual">Manual Entry</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Referral">Referral</option>
                    <option value="Job Board">Job Board</option>
                    <option value="Career Fair">Career Fair</option>
                    <option value="Agency">Agency</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newCandidate.notes}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Additional notes about the candidate..."
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingCandidate}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {addingCandidate ? 'Adding...' : 'Add Candidate'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidates;
